import arrayMutators from 'final-form-arrays';
import { func } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
// These relative imports need to point to correct directories
import { Button, FieldTextInput, Form } from '../../components';
import IconCollection from '../IconCollection/IconCollection';
import {
  FormattedMessage,
  intlShape
} from '../../util/reactIntl';
import css from "./MerchantDetails.module.css";

// as a template.
import ReCAPTCHA from 'react-google-recaptcha';
import useCaptcha from '../../hooks/useCaptcha/useCaptcha';
import { sendVerificationOtp } from '../../util/api';
import * as validators from '../../util/validators';

const CODE_LENGTH = 6;
const RESEND_OTP_COOLDOWN_SECONDS = 90;

const VerificationCode = props => {
  const {
    captchaToken,
    isVerified,
    errorMessage: captchaErrorMessage,
    onCaptchaChange,
  } = useCaptcha();

  const [time, setTime] = useState(RESEND_OTP_COOLDOWN_SECONDS);

  useEffect(() => {
    if (time === 0) return;

    // Set interval to decrease time every second
    const interval = setInterval(() => {
      setTime(prevTime => prevTime - 1);
    }, 1000);

    // Clear the interval when the component unmounts or timer reaches 0
    return () => clearInterval(interval);
  }, [time]);

  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  return (
    <FinalForm
      {...props}
      mutators={{
        ...arrayMutators,
      }}
      render={formRenderProps => {
        const {
          disabled,
          handleSubmit,
          intl,
          invalid,
          pristine,
          updated,
          formId,
          updateError,
          updateInProgress,
          values,
          form,
          successToaster,
          errorToaster,
          currentUser,
        } = formRenderProps;

        const { verificationCode } = values || {};

        const { protectedData, publicData } = currentUser?.attributes?.profile || {};
        const { phoneNumber } = protectedData || {};
        const { userType, isPhoneNumberVerified } = publicData || {};
        const formattedPhoneNumber = phoneNumber ? `...${phoneNumber.trim().slice(-4)}` : '';

        // codeRequiredMessage
        const codeRequiredMessage = intl.formatMessage({
          id: 'VerificationForm.codeRequiredMessage',
        });
        const codeRequired = validators.required(codeRequiredMessage);

        // invalidCodeLengthMessage
        const invalidCodeLengthMessage = intl.formatMessage(
          {
            id: 'VerificationForm.invalidCodeLengthMessage',
          },
          {
            codeLength: CODE_LENGTH,
          }
        );
        const invalidCodeLength = validators.fixLength(invalidCodeLengthMessage, CODE_LENGTH);

        const errorMessage = updateError ? (
          <p className={css.error}>
            <FormattedMessage id="VerificationForm.invalidOTP" />
          </p>
        ) : null;

        const submitReady = updated && pristine;
        const submitInProgress = updateInProgress;
        const submitDisabled =
          invalid || disabled || submitInProgress || !verificationCode || !isVerified;

        return (
          <Form onSubmit={handleSubmit} className={css.mainForm}>
            <div className={css.verifyBox}>
              <h3 className={css.verifyTitle}>
                <FormattedMessage id="VerificationForm.verifyPhoneNumber" />
              </h3>
              {isPhoneNumberVerified ? (
                <div className={css.verifiedState}>
                  <div className={css.verifiedBadge}>
                    <IconCollection name="check_mark" />
                    <span>
                      <FormattedMessage id="VerificationForm.phoneVerifiedBadge" />
                    </span>
                  </div>
                  <p className={css.verifySubTitle}>
                    <FormattedMessage
                      id="VerificationForm.phoneVerifiedDescription"
                      values={{ formattedPhoneNumber }}
                    />
                  </p>
                  <Button className={css.button} type="submit">
                    <FormattedMessage id="VerificationForm.continue" />
                  </Button>
                </div>
              ) : (
                <>
                  <p className={css.verifySubTitle}>
                    <FormattedMessage
                      id="VerificationForm.enterVerificationCode"
                      values={{ formattedPhoneNumber }}
                    />
                  </p>

                  <FieldTextInput
                    name="verificationCode"
                    type="number"
                    placeholder=""
                    className={css.inputField}
                    isRequired={true}
                    validate={validators.composeValidators(codeRequired, invalidCodeLength)}
                  />

                  <div className={css.captchaWrapper}>
                    <ReCAPTCHA
                      sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY}
                      onChange={onCaptchaChange}
                    />
                  </div>

                  {errorMessage ? errorMessage : null}

                  <Button
                    className={css.button}
                    type="submit"
                    inProgress={submitInProgress}
                    disabled={submitDisabled}
                    ready={submitReady}
                  >
                    <FormattedMessage id="VerificationForm.verifyPhoneNumber" />
                  </Button>

                  {time !== 0 ? (
                    <p className={css.resendOTP}>
                      <FormattedMessage
                        id="VerificationForm.resendOTP"
                        values={{ time: formatTime(time) }}
                      />
                    </p>
                  ) : null}

                  {time === 0 ? (
                    <Button
                      type="button"
                      className={css.resendOTPButton}
                      onClick={() => {
                        if (phoneNumber) {
                          sendVerificationOtp({ phoneNumber })
                            .then(res => {
                              setTime(RESEND_OTP_COOLDOWN_SECONDS);
                              alert(intl.formatMessage({ id: 'VerificationForm.resendOTPSuccess' }));
                            })
                            .catch(err => {
                              alert(intl.formatMessage({ id: 'VerificationForm.resendOTPError' }));
                            });
                        }
                      }}
                    >
                      {intl.formatMessage({ id: 'VerificationForm.resendOTPButtonLabel' })}
                    </Button>
                  ) : null}
                </>
              )}
            </div>
          </Form>
        );
      }}
    />
  );
};

VerificationCode.defaultProps = {
  selectedPlace: null,
  updateError: null,
};

VerificationCode.propTypes = {
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  //   updateInProgress: bool.isRequired,
};

export default VerificationCode;
